import { WeatherWidget } from '@/components/chat/tools/weather-widget/runtime';
import type { Toolkit } from '@assistant-ui/react';
import type { WebSearchToolResult } from '@iq/contracts/v1/tools';
import { WeatherWidgetPayload } from '@iq/contracts/v1/weather';
import { Badge, Card, CardContent, CardHeader, CardTitle, cn } from '@iq/ui';
import { BookOpen, CloudSun, ExternalLink, Globe, ImageIcon, LoaderIcon } from 'lucide-react';

// ── Web Search ──────────────────────────────────────────────────────────
// Card consumes the contract shape from `@iq/contracts/v1/tools`.

interface WebSearchArgs {
  query: string;
  maxResults?: number;
  depth?: 'basic' | 'advanced';
}

// ── Weather ─────────────────────────────────────────────────────────────
// Backend `get_weather` returns a WeatherWidgetPayload; the frontend spreads
// it straight into <WeatherWidget />.

interface WeatherArgs {
  query: string;
}

// ── Knowledge Base Search ───────────────────────────────────────────────

interface SearchKbArgs {
  query: string;
  excludeChunkIds?: string[];
}

interface SearchKbResult {
  results: Array<{
    chunkId: string;
    docTitle: string | null;
    text: string;
    hasImage: boolean;
  }>;
}

// ── Toolkit ─────────────────────────────────────────────────────────────

export const appToolkit: Toolkit = {
  web_search: {
    type: 'backend',
    render: ({ args, result, status }) => {
      const { query } = args as WebSearchArgs;
      const res = result as WebSearchToolResult | undefined;
      const running = status?.type === 'running';
      return (
        <Card className="my-2 w-full">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 py-2">
            {running ? (
              <LoaderIcon className="size-4 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <Globe className="size-4 shrink-0 text-muted-foreground" />
            )}
            <CardTitle className="text-sm font-medium">
              {running ? 'Searching the web…' : 'Web search'}
            </CardTitle>
            <Badge variant="outline" className={cn('ms-auto', running && 'animate-pulse')}>
              {query}
            </Badge>
          </CardHeader>
          {res && Array.isArray(res.results) && res.results.length > 0 ? (
            <CardContent className="flex flex-col gap-2 py-2">
              {res.results.slice(0, 5).map((r) => (
                <a
                  key={r.url}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col gap-0.5 rounded-md border border-transparent p-2 transition-colors hover:border-border hover:bg-accent/50"
                >
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <span className="truncate">{r.title}</span>
                    <ExternalLink className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{r.snippet}</p>
                </a>
              ))}
            </CardContent>
          ) : null}
        </Card>
      );
    },
  },

  get_weather: {
    type: 'backend',
    render: ({ args, result, status }) => {
      const { query } = (args as WeatherArgs | undefined) ?? { query: '' };
      const running = status?.type === 'running';
      // `result` is untrusted: while running it's undefined, and on a failed
      // tool react-ai-sdk sets it to `{ error: errorText }` — a truthy object a
      // bare `!result` guard misses. Parse against the contract so only a
      // well-formed payload reaches the widget; everything else (error, partial,
      // undefined) falls through to the "Weather unavailable" card.
      const parsed = WeatherWidgetPayload.safeParse(result);
      if (running || !parsed.success) {
        return (
          <Card className="my-2 w-full max-w-md">
            <CardHeader className="flex flex-row items-center gap-2 space-y-0 py-3">
              {running ? (
                <LoaderIcon className="size-4 shrink-0 animate-spin text-muted-foreground" />
              ) : (
                <CloudSun className="size-4 shrink-0 text-muted-foreground" />
              )}
              <CardTitle className="text-sm font-medium">
                {running ? `Fetching weather for ${query || 'location'}…` : 'Weather unavailable'}
              </CardTitle>
            </CardHeader>
          </Card>
        );
      }
      return <WeatherWidget {...parsed.data} className="my-2" />;
    },
  },

  search_knowledge_base: {
    type: 'backend',
    render: ({ args, result, status }) => {
      const { query } = args as SearchKbArgs;
      const res = result as SearchKbResult | undefined;
      const running = status?.type === 'running';
      const hits = res?.results ?? [];
      return (
        <Card className="my-2 w-full">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 py-2">
            {running ? (
              <LoaderIcon className="size-4 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <BookOpen className="size-4 shrink-0 text-muted-foreground" />
            )}
            <CardTitle className="text-sm font-medium">
              {running ? 'Refining knowledge-base search…' : 'Knowledge base'}
            </CardTitle>
            <Badge variant="outline" className={cn('ms-auto', running && 'animate-pulse')}>
              {query}
            </Badge>
          </CardHeader>
          {hits.length > 0 ? (
            <CardContent className="flex flex-col gap-2 py-2">
              {hits.slice(0, 4).map((h) => (
                <div
                  key={h.chunkId}
                  className="flex flex-col gap-0.5 rounded-md border border-transparent p-2 transition-colors hover:border-border hover:bg-accent/50"
                >
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <span className="truncate">{h.docTitle ?? 'Untitled document'}</span>
                    {h.hasImage ? (
                      <ImageIcon className="size-3 shrink-0 text-muted-foreground" />
                    ) : null}
                  </div>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{h.text}</p>
                </div>
              ))}
            </CardContent>
          ) : null}
        </Card>
      );
    },
  },
};
